// Caching for the per-song "Anhoeren" URL.
//
// Strategy: we let the service worker do the heavy lifting. A
// runtimeCaching rule in vite.config.ts intercepts every audio
// request (cross-origin or same-origin), serves it from CACHE_AUDIO
// when present, otherwise fetches from the network and stores it.
//
// This module exposes the explicit "cache this URL now" trigger
// (cacheListenUrl) and a status check (isListenUrlCached) that the
// ListenCachePrompt component uses to render its red/green state.
//
// useCachedAudioSrc just hands back the original URL — the service
// worker handles offline playback transparently.

import { useEffect, useState } from 'react'
import { OFFLINE_ENABLED, CACHE_AUDIO } from '../../config/offline'
import { requestPersistence } from './quota'

const SW_RUNTIME_CACHE = 'songbook-audio-v1'

async function openAudioCache(): Promise<Cache | null> {
  if (typeof caches === 'undefined') return null
  try {
    return await caches.open(SW_RUNTIME_CACHE)
  } catch {
    return null
  }
}

export async function isListenUrlCached(url: string): Promise<boolean> {
  if (!OFFLINE_ENABLED || !url) return false
  const cache = await openAudioCache()
  if (!cache) return false
  try {
    const match = await cache.match(url, { ignoreSearch: false, ignoreVary: true })
    return !!match
  } catch {
    return false
  }
}

export interface CacheProgress {
  receivedBytes: number
  totalBytes: number  // 0 when Content-Length is missing
  percent: number     // 0-100, or -1 when total is unknown
}

export async function cacheListenUrl(
  url: string,
  onProgress?: (p: CacheProgress) => void,
): Promise<void> {
  if (!OFFLINE_ENABLED || !url) return

  await requestPersistence()

  const cache = await openAudioCache()
  if (!cache) {
    throw new Error('Cache API not available')
  }

  // A CORS fetch is required: the audio element issues Range
  // requests when the user seeks / plays, and Range requests can
  // only be satisfied from non-opaque cache entries. If the host
  // does not send Access-Control-Allow-Origin the fetch fails and
  // we surface a clear error — the caller is expected to fix the
  // server, not work around it.
  let response: Response
  try {
    response = await fetch(url, { mode: 'cors' })
  } catch (err) {
    throw new Error(
      `CORS fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`,
    )
  }

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`)
  }

  if (response.type === 'opaque') {
    throw new Error('Host liefert keine CORS-Antwort')
  }

  const totalBytes = Number(response.headers.get('Content-Length') ?? 0)
  const contentType = response.headers.get('Content-Type') ?? 'audio/mpeg'

  // Stream the body so we can report progress while it downloads.
  // Without a readable body (rare), we fall back to the original
  // response and skip progress reporting.
  if (!response.body) {
    await cache.put(url, response)
  } else {
    const reader = response.body.getReader()
    const chunks: Uint8Array[] = []
    let received = 0

    onProgress?.({
      receivedBytes: 0,
      totalBytes,
      percent: totalBytes > 0 ? 0 : -1,
    })

    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      if (value) {
        chunks.push(value)
        received += value.length
        onProgress?.({
          receivedBytes: received,
          totalBytes,
          percent: totalBytes > 0 ? Math.round((received / totalBytes) * 100) : -1,
        })
      }
    }

    const blob = new Blob(chunks as BlobPart[], { type: contentType })
    const cachedResponse = new Response(blob, {
      status: 200,
      statusText: 'OK',
      headers: {
        'Content-Type': contentType,
        'Content-Length': String(blob.size),
        'Accept-Ranges': 'bytes',
      },
    })

    await cache.put(url, cachedResponse)
  }

  const stored = await cache.match(url)
  if (!stored) {
    throw new Error('Cache-Schreibvorgang fehlgeschlagen')
  }
}

export async function uncacheListenUrl(url: string): Promise<void> {
  if (!OFFLINE_ENABLED || !url) return
  const cache = await openAudioCache()
  if (!cache) return
  await cache.delete(url)
}

/**
 * Returns the audio src for the player. We always hand back the
 * original URL — the service worker intercepts the request and
 * serves it from CACHE_AUDIO when offline.
 */
export function useCachedAudioSrc(url: string): string {
  const [src, setSrc] = useState<string>(url)
  useEffect(() => setSrc(url), [url])
  return src
}

// Legacy export so older imports still resolve.
export { CACHE_AUDIO }
